import axios from '../lib/axios';
import { AxiosError } from 'axios';

/**
 * Grievance interface
 */
export interface Grievance {
  _id?: string;
  name: string;
  email: string;
  phone: string;
  reason: string;
  adminRemark?: string;   // match backend field
  createdAt?: string;
  updatedAt?: string;
  logs?: any[]; // assuming logs is an array, adjust type as needed
}

/**
 * Error Response interface
 */
interface ErrorResponse {
  message: string;
  statusCode?: number;
}

/**
 * Create a new grievance
 */
export const postGrievance = async (
  data: Omit<Grievance, "_id" | "createdAt" | "updatedAt" | "adminRemark">
) => {
  try {
    const response = await axios.post<{ message: string; data: Grievance }>(
      '/complaint/grievances',
      data
    );
    return response.data.data;
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      throw new Error(axiosError.response.data.message || 'Failed to create grievance');
    }
    throw new Error('Network error. Please try again.');
  }
};

/**
 * Get grievances (supports filters, pagination, sorting, search)
 */
export const getGrievances = async (params?: {
  phone?: string;
  id?: string;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  search?: string;
  from?: string; // ✅ changed to match backend
  to?: string;   // ✅ changed to match backend
}) => {
  try {
    const response = await axios.get<{
      message: string;
      data: Grievance[];
      total?: number;
      page?: number;
      pageSize?: number;
    }>("/complaint/grievances", { params });

    return response.data;
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      throw new Error(
        axiosError.response.data.message || "Failed to fetch grievances"
      );
    }
    throw new Error("Network error. Please try again.");
  }
};


/**
 * Update grievance remark by ID
 */
export const updateGrievanceRemark = async (id: string, adminRemark: string) => {
  try {
    const response = await axios.put<{ message: string; data: Grievance }>(
      `/complaint/grievances/${id}/remark`,
      { adminRemark }   // ✅ match backend
    );
    return response.data.data;
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      throw new Error(axiosError.response.data.message || 'Failed to update grievance remark');
    }
    throw new Error('Network error. Please try again.');
  }
};

export const getGrievanceById = async (id: string) => {
  try {
    const response = await axios.get<{ message: string; data: Grievance }>(
      "/complaint/grievances",
      { params: { id } }
    );
    return response.data.data;
  } catch (error) {
    const axiosError = error as AxiosError<ErrorResponse>;
    if (axiosError.response?.data) {
      throw new Error(axiosError.response.data.message || "Failed to fetch grievance");
    }
    throw new Error("Network error. Please try again.");
  }
};

